import getExclusionReason from '@app.raytd.com/common/utils/tests/getExclusionReason';
import { toast } from 'sonner';
import { Assessment } from '../assessment/assessment.slice';
import { AssetEntity } from '../asset';
import { TestCase, TestItem, TestSuite } from '../tests/entity';
import type { ReferenceImage, ReferenceImageDisposition } from '../tests/api/referenceImages';
import { TestSpecificationsEntity } from '../tests/test-specifications.slice';
import { UsersEntity } from '../users';
import { transformClassificationTree } from './elementTransformer';
import {
  ActivityItem,
  AddCustomFieldOptionRequest,
  AddCustomFieldOptionResponse,
  ClassificationItem,
  DashboardRecentResponse,
  FastFillUpdateRequest,
  GetTestItemRequest,
  PublishedReportResponse,
  PublishReportPayload,
  PublishReportResponse,
  ReportBlock,
  ReportEntity,
  ReportPayload,
  ReportResponse,
  ReportRevision,
  ReportSection,
  ReportTestItem,
  ReportUpdatePayload,
  ToggleSwitchRequest,
  ToggleSwitchResponse,
  TestItemComment,
} from './types';
import updateItemInReportCache from './updateItemInReportCache';
import {
  NotificationPagination,
  AppNotification,
  ReportExport,
} from '../notifications/types';
import { rAPI } from './rAPI';

export const usersAPI = rAPI.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getLoggedInUser: builder.query({
      query: () => 'user/profile',
      transformResponse: (response: { data: any }) => response.data,
      providesTags: ['User'],
    }),

    getTeamUsers: builder.query({
      query: () => 'users',
      transformResponse: (response: { users: any[] }) => response.users,
      providesTags: ['Users'],
    }),

    updateUserProfile: builder.mutation({
      query: (profile) => ({
        url: 'user/profile',
        method: 'PUT',
        body: profile,
      }),
      invalidatesTags: ['User'],
    }),

    getUserQualifications: builder.query({
      query: () => `qualifications`,
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ['Qualifications'],
    }),

    createUserQualification: builder.mutation({
      query: (qualification) => ({
        url: `qualifications`,
        method: 'POST',
        body: qualification,
      }),
      invalidatesTags: ['Qualifications'],
    }),

    updateUserQualification: builder.mutation({
      query: ({ qualification_id, ...qualification }) => ({
        url: `qualifications/${qualification_id}`,
        method: 'PUT',
        body: qualification,
      }),
      invalidatesTags: ['Qualifications'],
    }),

    deleteUserQualification: builder.mutation({
      query: (qualification_id) => ({
        url: `qualifications/${qualification_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Qualifications'],
    }),

    getOffices: builder.query({
      query: () => 'offices',
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ['Offices'],
    }),

    createOffice: builder.mutation({
      query: (office) => ({
        url: 'offices',
        method: 'POST',
        body: office,
      }),
      invalidatesTags: ['Offices'],
    }),

    deleteOffice: builder.mutation({
      query: (officeId) => ({
        url: `offices/${officeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Offices'],
    }),

    updateOffice: builder.mutation({
      query: ({ id, ...office }) => ({
        url: `offices/${id}`,
        method: 'PUT',
        body: office,
      }),
      invalidatesTags: ['Offices'],
    }),

    getAdminOrganisations: builder.query({
      query: () => 'admin/organisations',
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ['Organisations'],
    }),

    getAdminUsers: builder.query({
      query: (organisationId) =>
        organisationId ? `admin/users/${organisationId}` : 'admin/users',
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: ['Users'],
    }),

    deleteAdminUser: builder.mutation({
      query: ({ userId }) => ({
        url: `admin/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    getOrganisation: builder.query({
      query: (organisationId) => `organisations/${organisationId}`,
      transformResponse: (response: { data: any }) => response.data,
      providesTags: ['Organisations'],
    }),

    updateOrganisation: builder.mutation({
      query: ({ id, ...organisation }) => ({
        url: `organisations/${id}`,
        method: 'PUT',
        body: organisation,
      }),
      invalidatesTags: ['Organisations'],
    }),

    leaveOrganisation: builder.mutation({
      query: ({ id, user_id }) => ({
        url: `organisations/${id}/leave`,
        method: 'DELETE',
        body: {
          user_id: user_id,
        },
      }),
      invalidatesTags: [
        'Organisations',
        'Users',
        'Offices',
        'Asset',
        'Buildings',
        'Levels',
        'Areas',
        'User',
      ],
    }),

    inviteTeamMember: builder.mutation({
      query: (user) => ({
        url: 'organisations/invite',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: ['Users'],
    }),

    createUser: builder.mutation({
      query: (user) => ({
        url: 'register',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: ['Users'],
    }),

    updateUserRole: builder.mutation({
      query: ({ user_id, organisation_id, role }) => ({
        url: `users/${user_id}/organisations/${organisation_id}`,
        method: 'PUT',
        body: { role },
      }),
      invalidatesTags: ['Users'],
    }),

    getAuthors: builder.query<
      UsersEntity[],
      { assets: number[]; inspections: number[] }
    >({
      query: ({ assets, inspections }) => {
        const params = new URLSearchParams();
        assets.forEach((asset) => params.append('assets', asset.toString()));
        inspections.forEach((inspection) =>
          params.append('inspections', inspection.toString())
        );
        return {
          url: `authors?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: { data: UsersEntity[] }) => response.data,
    }),
  }),
});

export const {
  useGetLoggedInUserQuery,
  useGetTeamUsersQuery,
  useUpdateUserProfileMutation,
  useGetUserQualificationsQuery,
  useCreateUserQualificationMutation,
  useUpdateUserQualificationMutation,
  useDeleteUserQualificationMutation,
  useGetOfficesQuery,
  useCreateOfficeMutation,
  useDeleteOfficeMutation,
  useUpdateOfficeMutation,
  useGetOrganisationQuery,
  useUpdateOrganisationMutation,
  useLeaveOrganisationMutation,
  useInviteTeamMemberMutation,
  useCreateUserMutation,
  useUpdateUserRoleMutation,
  useGetAdminOrganisationsQuery,
  useGetAdminUsersQuery,
  useDeleteAdminUserMutation,
  useGetAuthorsQuery,
} = usersAPI;
